import { useMemo, useState } from "react";
import { Button } from "@repo/ui";
import { X, ChevronDown, ChevronRight, Loader2 } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@repo/ui";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@repo/ui";
import { Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { AroTypeFilter, PyloAro } from "@/services/acl.service";
import { useAro } from "@/hooks/auth/use-acl";
import {
  useAroChildren,
  useParentAro,
  useRootAros,
  useSearchAros,
} from "@/hooks/auth/use-aro-tree";

// Entity-view sharing targets dashboard users + any non-user entity instance,
// but never storefront app users. We keep the `AppUser` entity out server-side
// so pagination stays correct (client-side filtering would punch holes in pages).
const EXCLUDED_ENTITY_NAMES = ["AppUser"];
// Type filter is fixed to "users": this excludes API-key AROs while still
// returning plain entity instances (which carry no API key).
const TYPE_FILTER: AroTypeFilter = "users";

const getApiKeyName = (aro: PyloAro): string | undefined =>
  aro.pylo_users?.data?.[0]?.pylo_api_key?.data?.name;

// A single selectable (child) ARO row. Users render by email/resolved display
// name; the shared `useAro` hook resolves an instance's display field.
const AroCommandItem: React.FC<{
  aro: PyloAro;
  isSelected: boolean;
  onSelect: () => void;
}> = ({ aro, isSelected, onSelect }) => {
  const { displayName, isLoading } = useAro(aro);
  const apiKeyName = getApiKeyName(aro);
  const label = apiKeyName ? `api key: "${apiKeyName}"` : isLoading ? "Loading..." : displayName;

  return (
    <CommandItem value={aro.id} onSelect={onSelect} className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        <Check className={cn("h-4 w-4", isSelected ? "opacity-100" : "opacity-0")} />
        <span className="truncate">{label}</span>
      </div>
    </CommandItem>
  );
};

// A selected ARO shown as a removable chip. Resolves the ARO row by id
// (react-query cached) so already-saved selections render even before their
// entity group has been expanded/loaded.
const SelectedAroChip: React.FC<{
  id: string;
  onRemove: () => void;
}> = ({ id, onRemove }) => {
  const { data: aro, isLoading } = useParentAro({ parentId: id, enabled: true });
  const { displayName } = useAro(aro ?? undefined);

  const apiKeyName = aro ? getApiKeyName(aro) : undefined;
  const finalDisplayName = apiKeyName
    ? `api key: "${apiKeyName}"`
    : !aro && isLoading
      ? "Loading..."
      : displayName;

  return (
    <div className="flex items-center justify-between rounded-md border p-2">
      <div className="flex items-center gap-2">
        <ChevronRight className="h-3 w-3 text-muted-foreground" />
        <div className="flex flex-col">
          <span className="text-sm font-medium">{finalDisplayName}</span>
          <span className="text-xs text-muted-foreground">{aro?.entity?.data.name || ""}</span>
        </div>
      </div>
      <Button variant="ghost" size="sm" onClick={onRemove} className="h-6 w-6 p-0">
        <X className="h-3 w-3" />
      </Button>
    </div>
  );
};

const LoadMoreRow: React.FC<{ onClick: () => void; isLoading: boolean }> = ({
  onClick,
  isLoading,
}) => (
  <div className="px-2 py-1">
    <Button
      variant="ghost"
      size="sm"
      className="h-7 w-full justify-center text-xs text-muted-foreground"
      onClick={onClick}
      disabled={isLoading}
    >
      {isLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : "Load more"}
    </Button>
  </div>
);

// Lazily loads and renders the instances (child AROs) of one entity group.
const EntityGroupChildren: React.FC<{
  parentId: string;
  selectedIds: Set<string>;
  onToggle: (aroId: string) => void;
}> = ({ parentId, selectedIds, onToggle }) => {
  const { children, isLoading, hasMore, fetchMore, isFetchingMore } = useAroChildren({
    parentId,
    typeFilter: TYPE_FILTER,
    enabled: true,
    excludeEntityNames: EXCLUDED_ENTITY_NAMES,
  });

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 px-2 py-1 text-xs text-muted-foreground">
        <Loader2 className="h-3 w-3 animate-spin" /> Loading…
      </div>
    );
  }

  if (children.length === 0) {
    return <div className="px-2 py-1 text-xs text-muted-foreground">No entries.</div>;
  }

  return (
    <>
      {children.map((child) => (
        <AroCommandItem
          key={child.id}
          aro={child}
          isSelected={selectedIds.has(child.id)}
          onSelect={() => onToggle(child.id)}
        />
      ))}
      {hasMore && <LoadMoreRow onClick={() => fetchMore()} isLoading={isFetchingMore} />}
    </>
  );
};

interface PyloAroMultiSelectProps {
  selectedAros: Array<{ id: string }>;
  onSelectionChange: (aros: Array<{ id: string }>) => void;
  placeholder?: string;
}

export const PyloAroMultiSelect: React.FC<PyloAroMultiSelectProps> = ({
  selectedAros,
  onSelectionChange,
  placeholder = "Select users...",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [expandedRootIds, setExpandedRootIds] = useState<Set<string>>(new Set());

  const isSearchMode = searchValue.trim().length >= 2;

  const selectedAroIds = useMemo(() => new Set(selectedAros.map((aro) => aro.id)), [selectedAros]);

  // Browse mode: entity-group root AROs, expanded on demand.
  const rootQuery = useRootAros({
    enabled: isOpen && !isSearchMode,
    excludeEntityNames: EXCLUDED_ENTITY_NAMES,
  });
  const rootAros = useMemo(
    () => rootQuery.data?.pages.flatMap((page) => page.data) ?? [],
    [rootQuery.data],
  );

  // Search mode: server-side typeahead over user AROs (matched by email).
  const searchQuery = useSearchAros({
    searchTerm: searchValue.trim(),
    typeFilter: TYPE_FILTER,
    excludeEntityNames: EXCLUDED_ENTITY_NAMES,
    enabled: isOpen && isSearchMode,
  });
  const searchResults = useMemo(
    () => searchQuery.data?.pages.flatMap((page) => page.data) ?? [],
    [searchQuery.data],
  );

  const handleToggle = (aroId: string) => {
    if (selectedAroIds.has(aroId)) {
      onSelectionChange(selectedAros.filter((aro) => aro.id !== aroId));
    } else {
      onSelectionChange([...selectedAros, { id: aroId }]);
    }
  };

  const toggleRoot = (rootId: string) => {
    setExpandedRootIds((prev) => {
      const next = new Set(prev);
      if (next.has(rootId)) next.delete(rootId);
      else next.add(rootId);
      return next;
    });
  };

  return (
    <div className="space-y-2">
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={isOpen}
            className="w-full justify-between"
          >
            {selectedAros.length === 0 ? placeholder : `${selectedAros.length}  selected`}
            <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[400px] p-0">
          {/* Results are filtered server-side; disable cmdk's client filter so it
              doesn't hide rows whose display label differs from the search term. */}
          <Command shouldFilter={false}>
            <CommandInput
              placeholder="Search users…"
              value={searchValue}
              onValueChange={setSearchValue}
            />
            <CommandList>
              {isSearchMode ? (
                searchQuery.isLoading ? (
                  <div className="flex items-center gap-2 px-3 py-3 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" /> Searching…
                  </div>
                ) : searchResults.length === 0 ? (
                  <CommandEmpty>No users found.</CommandEmpty>
                ) : (
                  <CommandGroup>
                    {searchResults.map((aro) => (
                      <AroCommandItem
                        key={aro.id}
                        aro={aro}
                        isSelected={selectedAroIds.has(aro.id)}
                        onSelect={() => handleToggle(aro.id)}
                      />
                    ))}
                    {searchQuery.hasNextPage && (
                      <LoadMoreRow
                        onClick={() => searchQuery.fetchNextPage()}
                        isLoading={searchQuery.isFetchingNextPage}
                      />
                    )}
                  </CommandGroup>
                )
              ) : rootQuery.isLoading ? (
                <div className="flex items-center gap-2 px-3 py-3 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" /> Loading…
                </div>
              ) : rootAros.length === 0 ? (
                <CommandEmpty>No entities found.</CommandEmpty>
              ) : (
                <>
                  {rootAros.map((root) => {
                    const isExpanded = expandedRootIds.has(root.id);
                    return (
                      <CommandGroup key={root.id}>
                        <CommandItem
                          value={root.id}
                          onSelect={() => toggleRoot(root.id)}
                          className="flex items-center gap-2 font-medium"
                        >
                          {isExpanded ? (
                            <ChevronDown className="h-4 w-4" />
                          ) : (
                            <ChevronRight className="h-4 w-4" />
                          )}
                          <span className="truncate">{root.entity?.data.name || "Unknown"}</span>
                        </CommandItem>
                        {isExpanded && (
                          <div className="ml-4">
                            <EntityGroupChildren
                              parentId={root.id}
                              selectedIds={selectedAroIds}
                              onToggle={handleToggle}
                            />
                          </div>
                        )}
                      </CommandGroup>
                    );
                  })}
                  {rootQuery.hasNextPage && (
                    <LoadMoreRow
                      onClick={() => rootQuery.fetchNextPage()}
                      isLoading={rootQuery.isFetchingNextPage}
                    />
                  )}
                </>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {/* Selected AROs display */}
      {selectedAros.length > 0 && (
        <div className="max-h-[200px] space-y-1 overflow-y-auto">
          {selectedAros.map((aro) => (
            <SelectedAroChip key={aro.id} id={aro.id} onRemove={() => handleToggle(aro.id)} />
          ))}
        </div>
      )}
    </div>
  );
};
